import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { centsToBrlInput, formatBrlCurrency, parseBrlToCents } from "@/lib/price";
import type { RawItem } from "@/types/models";
import type { StorePricingMode } from "@/lib/pricing";
import {
  ProductBillOfMaterialsEditor,
  bomLinesToInput,
  effectiveCostFromBomLines,
  type ProductBomLine,
} from "./ProductBillOfMaterialsEditor";
import { PricingHelperPanel } from "./PricingHelperPanel";
import { PricingSectionGroup } from "./PricingSectionGroup";
import type { ProductPricingFormValues } from "../lib/productPricingInput";

export interface ProductPricingSectionProps {
  rawItems: RawItem[];
  bomLines: ProductBomLine[];
  onBomLinesChange: (lines: ProductBomLine[]) => void;
  values: ProductPricingFormValues;
  onChange: (values: ProductPricingFormValues) => void;
  storeMarkupPercent?: number | null;
  storeTargetNetMarginPercent?: number | null;
  storePricingMode?: StorePricingMode | null;
  storeTaxPercent?: number | null;
}

export function ProductPricingSection({
  rawItems,
  bomLines,
  onBomLinesChange,
  values,
  onChange,
  storeMarkupPercent,
  storeTargetNetMarginPercent,
  storePricingMode,
  storeTaxPercent,
}: ProductPricingSectionProps) {
  const hasBom = bomLinesToInput(bomLines).length > 0;

  const materialCostCents = useMemo(() => {
    if (hasBom) {
      return effectiveCostFromBomLines(rawItems, bomLines);
    }
    if (values.cost.trim() === "") return null;
    const cents = parseBrlToCents(values.cost);
    return Number.isNaN(cents) ? null : cents;
  }, [hasBom, bomLines, rawItems, values.cost]);

  const staticCostCents = useMemo(() => {
    if (values.staticCost.trim() === "") return 0;
    const cents = parseBrlToCents(values.staticCost);
    return Number.isNaN(cents) ? 0 : cents;
  }, [values.staticCost]);

  const effectiveCostCents = useMemo(() => {
    if (materialCostCents == null && staticCostCents === 0) return null;
    return (materialCostCents ?? 0) + staticCostCents;
  }, [materialCostCents, staticCostCents]);

  const updateValues = (patch: Partial<ProductPricingFormValues>) => {
    onChange({ ...values, ...patch });
  };

  const handleUseSuggestedPrice = (priceCents: number) => {
    updateValues({ price: centsToBrlInput(priceCents) });
  };

  return (
    <div className="space-y-6" data-testid="product-pricing-section">
      <PricingSectionGroup
        testId="pricing-section-final-price"
        title="Preço de venda"
        description="Valor que o cliente verá no catálogo."
        variant="emphasis"
      >
        <div className="max-w-sm space-y-2">
          <label
            htmlFor="pricing-price"
            className="text-sm font-light leading-none text-primary-900 dark:text-white"
          >
            Preço final (R$)
          </label>
          <Input
            id="pricing-price"
            value={values.price}
            onChange={(e) => updateValues({ price: e.target.value })}
            placeholder="199,90"
            inputMode="decimal"
            className="h-12 text-base"
          />
        </div>
      </PricingSectionGroup>

      <PricingSectionGroup
        testId="pricing-section-parameters"
        title="Precificação"
        description="Markup, margem e impostos para calcular o preço sugerido."
      >
        <PricingHelperPanel
          embedded
          effectiveCostCents={effectiveCostCents}
          pricingMode={storePricingMode}
          markupPercent={values.markupPercent}
          targetNetMarginPercent={values.targetNetMarginPercent}
          taxPercent={values.effectiveTaxPercent}
          storeDefaultMarkupPercent={storeMarkupPercent}
          storeDefaultTargetNetMarginPercent={storeTargetNetMarginPercent}
          storeDefaultTaxPercent={storeTaxPercent}
          onPricingPercentsChange={({ markupPercent, targetNetMarginPercent }) =>
            updateValues({ markupPercent, targetNetMarginPercent })
          }
          onTaxPercentChange={(effectiveTaxPercent) => updateValues({ effectiveTaxPercent })}
          onUseSuggestedPrice={handleUseSuggestedPrice}
        />
      </PricingSectionGroup>

      <PricingSectionGroup
        testId="pricing-section-costs"
        title="Custos e receita"
        description="Componha a receita ou informe custos manualmente."
      >
        <ProductBillOfMaterialsEditor
          rawItems={rawItems}
          lines={bomLines}
          onChange={onBomLinesChange}
          showHeader={false}
        />

        <div className="border-t border-primary-200 dark:border-primary-800 pt-6 space-y-6">
          {hasBom ? (
            <div data-testid="material-cost-display">
              <p className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-1">
                Custo de insumos (receita)
              </p>
              <p className="text-sm font-light text-primary-900 dark:text-white">
                {materialCostCents != null ? formatBrlCurrency(materialCostCents) : "—"}
              </p>
            </div>
          ) : (
            <div className="max-w-xs space-y-2">
              <label
                htmlFor="pricing-cost"
                className="text-sm font-light leading-none text-primary-900 dark:text-white"
              >
                Custo de insumos (opcional)
              </label>
              <Input
                id="pricing-cost"
                value={values.cost}
                onChange={(e) => updateValues({ cost: e.target.value })}
                placeholder="Ex: 25,00"
                inputMode="decimal"
              />
              <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                Use quando o produto não tiver receita de insumos.
              </p>
            </div>
          )}

          <div className="max-w-xs space-y-2">
            <label
              htmlFor="pricing-static-cost"
              className="text-sm font-light leading-none text-primary-900 dark:text-white"
            >
              Custo fixo por unidade (opcional)
            </label>
            <Input
              id="pricing-static-cost"
              value={values.staticCost}
              onChange={(e) => updateValues({ staticCost: e.target.value })}
              placeholder="Ex: 5,00"
              inputMode="decimal"
            />
            <p className="text-xs font-light text-primary-500 dark:text-primary-500">
              Tempo, energia, aluguel e outros custos fixos agregados por item produzido.
            </p>
          </div>

          {effectiveCostCents != null && effectiveCostCents > 0 && (
            <div
              data-testid="effective-cost-display"
              className="border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950/30 p-4 space-y-2"
            >
              <p className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400">
                Custo efetivo total
              </p>
              <dl className="text-sm font-light space-y-1">
                {materialCostCents != null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-primary-600 dark:text-primary-400">Insumos</dt>
                    <dd className="text-primary-900 dark:text-white">
                      {formatBrlCurrency(materialCostCents)}
                    </dd>
                  </div>
                )}
                {staticCostCents > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-primary-600 dark:text-primary-400">Custo fixo</dt>
                    <dd className="text-primary-900 dark:text-white">
                      {formatBrlCurrency(staticCostCents)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-4 border-t border-primary-200 dark:border-primary-800 pt-2">
                  <dt className="text-primary-800 dark:text-white">Total para precificação</dt>
                  <dd className="text-primary-900 dark:text-white font-normal">
                    {formatBrlCurrency(effectiveCostCents)}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </PricingSectionGroup>
    </div>
  );
}
